/**
 * Calculates the total number of pages based on total items and items per page
 */
export const calculateTotalPages = (
  totalItems: number,
  itemsPerPage: number,
): number => {
  return Math.ceil(totalItems / itemsPerPage)
}

/**
 * Generates an array of page numbers to display in pagination UI
 * Includes ellipsis (represented by null) when there are many pages
 *
 * @param currentPage - The current active page
 * @param totalPages - Total number of pages
 * @param maxPagesToShow - Maximum number of page buttons to display (default: 5)
 * @returns Array of page numbers with null representing ellipsis
 */
export const getPageNumbers = (
  currentPage: number,
  totalPages: number,
  maxPagesToShow: number = 5,
): (number | null)[] => {
  const pages: (number | null)[] = []

  if (totalPages <= maxPagesToShow) {
    // If total pages less than max to show, display all pages
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // Always include first page, last page, and current page
    // Then add pages before and after current page
    const leftSide = Math.floor(maxPagesToShow / 2)
    const rightSide = maxPagesToShow - leftSide - 1

    // If current page is close to start
    if (currentPage <= leftSide + 1) {
      for (let i = 1; i <= maxPagesToShow - 1; i++) {
        pages.push(i)
      }
      pages.push(null) // Represents ellipsis
      pages.push(totalPages)
    }
    // If current page is close to end
    else if (currentPage >= totalPages - rightSide) {
      pages.push(1)
      pages.push(null) // Represents ellipsis
      for (let i = totalPages - maxPagesToShow + 2; i <= totalPages; i++) {
        pages.push(i)
      }
    }
    // If current page is in the middle
    else {
      pages.push(1)
      pages.push(null) // Represents ellipsis
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i)
      }
      pages.push(null) // Represents ellipsis
      pages.push(totalPages)
    }
  }

  return pages
}

/**
 * Calculates skip value for pagination API calls
 */
export const calculateSkip = (
  currentPage: number,
  itemsPerPage: number,
): number => {
  return (currentPage - 1) * itemsPerPage
}
